import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Trash2, Upload, AlertCircle, Calendar, Users, Building2, TrendingUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ManageUploads() {
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    fetchUploads();
  }, []);

  const fetchUploads = async () => {
    try {
      const response = await axios.get(`${API}/uploads`);
      setUploads(response.data);
    } catch (error) {
      toast.error('Failed to load uploads');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (upload) => {
    const key = `${upload.type}-${upload.created_at}`;
    setDeleting(key);
    try {
      const response = await axios.delete(`${API}/uploads/${upload.type}/${encodeURIComponent(upload.created_at)}`);
      const deletedCount = response.data.visits_deleted || response.data.records_deleted;
      toast.success(`Deleted ${deletedCount} ${upload.type === 'projections' ? 'projections' : 'visits'}`);
      setConfirmDelete(null);
      fetchUploads();
    } catch (error) {
      toast.error('Failed to delete upload');
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getUploadKey = (upload) => `${upload.type}-${upload.created_at}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-muted-foreground">Loading uploads...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">Manage Uploads</h1>
          <p className="text-muted-foreground mt-1">
            View and delete uploaded data batches
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Upload size={16} />
          {uploads.length} upload{uploads.length !== 1 ? 's' : ''}
        </div>
      </div>

      {uploads.length === 0 ? (
        <Card className="p-12 text-center">
          <Upload className="mx-auto text-muted-foreground mb-4" size={48} />
          <h3 className="text-lg font-semibold mb-2">No Uploads Yet</h3>
          <p className="text-muted-foreground">
            Upload data from the Upload Data page to see it here.
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {uploads.map((upload) => {
            const uploadKey = getUploadKey(upload);
            const isProjection = upload.type === 'projections';
            return (
            <Card key={uploadKey} className={`p-6 ${isProjection ? 'border-l-4 border-l-blue-500' : 'border-l-4 border-l-green-500'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-6">
                  <div className={`p-3 rounded-lg ${isProjection ? 'bg-blue-100' : 'bg-green-100'}`}>
                    {isProjection ? (
                      <TrendingUp className="text-blue-600" size={24} />
                    ) : (
                      <Calendar className="text-green-600" size={24} />
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-lg">
                        {formatDate(upload.created_at)}
                      </h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${isProjection ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                        {isProjection ? 'Projections' : 'Visits'}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        {isProjection ? <TrendingUp size={14} /> : <Upload size={14} />}
                        {upload.count} {isProjection ? 'projections' : 'visits'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users size={14} />
                        {upload.therapist_count} therapists
                      </span>
                      <span className="flex items-center gap-1">
                        <Building2 size={14} />
                        {upload.agency_count} agencies
                      </span>
                    </div>
                  </div>
                </div>
                
                <div>
                  {confirmDelete === uploadKey ? (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-destructive mr-2">Are you sure?</span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setConfirmDelete(null)}
                        disabled={deleting === uploadKey}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDelete(upload)}
                        disabled={deleting === uploadKey}
                      >
                        {deleting === uploadKey ? 'Deleting...' : 'Yes, Delete'}
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-destructive border-destructive hover:bg-destructive/10"
                      onClick={() => setConfirmDelete(uploadKey)}
                    >
                      <Trash2 size={16} className="mr-2" />
                      Delete Upload
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          )})}
        </div>
      )}

      <Card className="p-4 bg-amber-50 border-amber-200">
        <div className="flex items-start gap-3">
          <AlertCircle className="text-amber-600 mt-0.5" size={20} />
          <div>
            <p className="text-sm font-medium text-amber-800">Warning</p>
            <p className="text-sm text-amber-700">
              Deleting an upload will permanently remove all records from that batch. 
              This action cannot be undone.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}
